import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { DataPanel } from "react-migration/components/DataPanel";
import { useVariants } from "react-migration/components/TextWithVariants";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { KeyIndicatorColor, useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { getAreaVariants } from "react-migration/lib/util/getAreaAndPerimeter";
import { userStore } from "src/js/stores/user/store";
import { LayerTypeLayerSummaryProps, SupportedRegions } from "../../types";
import { usePropertyInformationByUPRNQuery } from "react-migration/domains/ownership/apollo/usePropertyInformationByUPRNQuery";

export function OwnershipBuildingSizeLayerSummaryUS({ selection }: LayerTypeLayerSummaryProps) {
  const { user } = useSnapshot(userStore);
  const { t } = useTranslation();
  const { propertyInformation } = usePropertyInformationByUPRNQuery(selection.id);
  const { area_building_metric, coverage, floor_area_ratio } = propertyInformation?.dimensions || {};

  const floorAreaMeasurements = useMemo(() => {
    return {
      floorAreaIsLarge: !!(area_building_metric && area_building_metric > 1000),
      floorAreaVariants: getAreaVariants(area_building_metric, user.settings.unit),
    };
  }, [area_building_metric, user.settings.unit]);

  const [floorAreaVariant, toggleFloorAreaVariant] = useVariants(
    floorAreaMeasurements.floorAreaVariants,
    floorAreaMeasurements.floorAreaIsLarge
  );

  useKeyIndicatorTags(() => {
    return [
      {
        tag: floorAreaVariant,
        color: KeyIndicatorColor.DEFAULT,
      },
    ];
  }, [floorAreaVariant]);

  return (
    <DataPanel.Grid columns={2}>
      <DataPanel.Cell title={t("property_information.sidebar.building_area")}>
        <DataPanel.Button onClick={toggleFloorAreaVariant}>{floorAreaVariant}</DataPanel.Button>
      </DataPanel.Cell>
      <DataPanel.Cell title={t("property_information.sidebar.coverage")}>
        {area_building_metric && coverage ? `${(coverage * 100).toFixed(2)}%` : t("not_applicable.long")}
      </DataPanel.Cell>
      <DataPanel.Cell title={t("property_information.sidebar.floor_area_ratio")}>
        {floor_area_ratio ? floor_area_ratio.toFixed(2) : t("not_applicable.long")}
      </DataPanel.Cell>
      <DataPanel.Cell title={t("property_information.sidebar.year_built")}>
        {propertyInformation?.year_built || t("not_applicable.long")}
      </DataPanel.Cell>
    </DataPanel.Grid>
  );
}

OwnershipBuildingSizeLayerSummaryUS.icon = "icon-lt-home-line";
OwnershipBuildingSizeLayerSummaryUS.hideLink = true;
OwnershipBuildingSizeLayerSummaryUS.supportedRegions = [SupportedRegions.us];
